
import { Component, Vue } from "vue-property-decorator";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import { Recipe } from "@/models/Recipes/Recipe";
import RecipesRepository from "@/utils/API/RecipesRepository";
import vPreLoader from "@/components/vPreLoader.vue";
import vModal from "@/components/vModal.vue";
import {downloadURI, mapNutritions} from "@/utils/Helpers";
import { desanitizeString } from "@/utils/Helpers";
import vSelectFx from "@/components/vSelectFx.vue";
import { LocalizationState } from "@/store/modules/LocalizationModule";
import vRecipeListing from "@/components/vRecipeListing.vue";
import vRecipeDetails from "@/components/vRecipeDetails.vue";
import vAuditLogModal from "@/components/vAuditLogModal.vue";
import vCopyRecipeModal from "@/components/vCopyRecipeModal.vue";
import vBreakdownReportModal from "@/components/vBreakdownReportModal.vue";
import VAminoAcidScoreReport from "@/components/reports/vAminoAcidScoreReport.vue";
import vSingleNutrientReportModal from "@/components/reports/vSingleNutrientReportModal.vue";
import { ItemCompact } from "@/models/ItemCompact";
import vProteinQualityReport from "@/components/reports/vProteinQualityReport.vue";
import { resource as recipeResource } from "@/utils/API/RecipesRepository";
import vRecipeDeleteConfirmationModal from "@/components/vRecipeDeleteConfirmationModal.vue";
import {ErrorResponse} from "@/utils/API/ResultTypes/ErrorResponse";
import vMultiColumnReportModal from "@/components/vMultiColumnReportModal.vue";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import {AccountState} from "@/store/modules/AccountModule";

interface RecipesState {
  recipe: Recipe;
  selectedRecipe: ItemCompact;
}

@Component({
  components: {
    VUpsellModal,
    VAminoAcidScoreReport,
    vBreadcrumbs,
    vPreLoader,
    vModal,
    vSelectFx,
    vRecipeListing,
    vRecipeDetails,
    vAuditLogModal,
    vCopyRecipeModal,
    vBreakdownReportModal,
    vSingleNutrientReportModal,
    vProteinQualityReport,
    vRecipeDeleteConfirmationModal,
    vMultiColumnReportModal
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
  methods: {
    desanitizeString
  }
})
class Recipes extends Vue {
  localizationState: LocalizationState;
  accountState: AccountState;

  recipesState: RecipesState = {
    recipe: new Recipe(),
    selectedRecipe: {
      id: 0,
      name: "",
      createdOn: null,
      updatedOn: null,
      createdUserName: "",
      updatedUserName: ""
    },
  };

  recipeForCopy: Recipe = new Recipe();
  checkedRecipesForDelete: number[] = [];
  funcHandler :any;

  // Modal states
  showDeleteRecipeModal = false;
  showCopyRecipeModal = false;
  showBreakdownReportModal = false;
  showProteinQualityReportModal = false;
  showAminoAcidScoreReportModal = false;
  showSingleNutrientReportModal = false;
  showMultiColumnReportModal = false;

  /**
   * Indicates whether the "Download Bulk Recipe Report" button is disabled.
   *
   * When set to `true`, the button is disabled and cannot be interacted with by the user.
   * When set to `false`, the button is enabled and available for user interaction.
   */
  isDownloadBulkRecipeReportButtonDisabled: boolean = false;

  // Default selected export type
  // "0" - Summary
  // "1" - Breakdown
  exportType: any = "0";

  // Default selected export country
  // "0" - Canada
  // "1" - US
  // "2" - UK
  exportCountryType: string = "0";

  showTrialUpsellModal: boolean = false;
  defaultTrialUpsellMessage = "This feature requires a paid subscription to use.";
  trialUpsellMessage = this.defaultTrialUpsellMessage;
  isRecipeDetailsLoading = false;
  lastRecipeDetailsRequestId = 0;

  openTrialUpsellModal(message: string = this.defaultTrialUpsellMessage) {
    this.trialUpsellMessage = message;
    this.showTrialUpsellModal = true;
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
    this.trialUpsellMessage = this.defaultTrialUpsellMessage;
  }

  async handleAddRecipeClick() {
    await this.$router.push({ name: "recipesAdd" });
  }

  /**
   * Binding export type with SelectFx
   * */
  changeDownloadType(id: any) {
    this.exportType = id;
  }

  print() {
    window.print();
  }

  openReport(id: number) {
    if (this.accountState.userDetails.isTrial) {
      this.openTrialUpsellModal();
      return;
    }

    switch (id) {
    case 0:
      this.openRecipeReportModal();
      break;
    case 1:
      this.openProteinQualityReportModal();
      break;
    case 2:
      this.openAminoAcidScoreReportModal();
      break;
    case 3:
      this.openSingleNutrientReportModal();
      break;
    case 4:
      this.openMultiColumnReportModal();
    }
  }

  /**
   * Handles the process of downloading a report. If the user is on a trial account,
   * it triggers an upsell modal. Otherwise, it proceeds to download the report.
   *
   * @return {void} No return value. Executes operations based on user account state.
   */
  async handleReportDownload() {
    if (this.accountState.userDetails.isTrial) {
      this.openTrialUpsellModal();
      return;
    }

    const reportType = this.exportType === "0" ? "Summary" : "Breakdown";

    let country = "";

    switch (this.exportCountryType) {
    case "0":
      country = "Canada";
      break;

    case "1":
      country = "USA";
      break;

    case "2":
      country = "UK";
      break;
    }

    this.isDownloadBulkRecipeReportButtonDisabled = true;
    await downloadURI(this.exportToExcelURL, `Recipes ${reportType} (${country})`);
    this.isDownloadBulkRecipeReportButtonDisabled = false;
  }

  // Open modal and view the recipe report
  openRecipeReportModal() {
    this.showBreakdownReportModal = true;
  }

  openProteinQualityReportModal() {
    this.showProteinQualityReportModal = true;
  }

  hideProteinQualityReportModal() {
    this.showProteinQualityReportModal = false;
  }

  openAminoAcidScoreReportModal() {
    this.showAminoAcidScoreReportModal = true;
  }

  hideAminoAcidScoreReportModal() {
    this.showAminoAcidScoreReportModal = false;
  }

  openSingleNutrientReportModal() {
    this.showSingleNutrientReportModal = true;
  }

  hideSingleNutrientReportModal() {
    this.showSingleNutrientReportModal = false;
  }

  openMultiColumnReportModal(){
    this.showMultiColumnReportModal = true;
  }

  hideMultiColumnReportModal(){
    this.showMultiColumnReportModal = false;
  }

  changeCountryForExport(id: any) {
    this.exportCountryType = id;
  }

  openDeleteModal(recipeId: number[], handler: (recipeIds: number[]) => void): void {
    this.checkedRecipesForDelete = recipeId;
    this.showDeleteRecipeModal = true;
    this.funcHandler = handler;
  }

  openCopyModal() {
    this.showCopyRecipeModal = true;
    RecipesRepository.getRecipeDetails(this.recipesState.recipe.id).then(
      (response) => {
        this.recipeForCopy = response.data;
        this.recipeForCopy.nutritionAnalysis = mapNutritions(response.data.nutritionAnalysis);
      }
    );
  }

  onAuditLogClick(recipe: ItemCompact) {
    this.auditLogModal.isOpen = true;
    this.recipesState.selectedRecipe = { ...recipe };
  }

  cancelAuditModal(){
    this.auditLogModal.isOpen = false;
  }

  /**
   * Click handler for showing selected recipe
   * */
  async chooseRecipe(recipeId: number) {
    this.isRecipeDetailsLoading = true;
    const requestId = ++this.lastRecipeDetailsRequestId;

    try {
      const response = await RecipesRepository.getRecipeDetails(recipeId, true);

      if (requestId !== this.lastRecipeDetailsRequestId) {
        return;
      }

      this.recipesState.recipe = { ...response.data };
      this.recipesState.recipe.nutritionAnalysis = mapNutritions(response.data.nutritionAnalysis);
    }
    catch (error: any) {
      if (requestId !== this.lastRecipeDetailsRequestId) {
        return;
      }

      if (error.response) {
        this.$snotify.error(error.response.data?.errorMessages?.[0]?.userVisibleMessage || error.response.data?.message);
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }
    }
    finally {
      if (requestId === this.lastRecipeDetailsRequestId) {
        this.isRecipeDetailsLoading = false;
      }
    }
  }

  saveCopiedRecipe(newRecipeName : string) {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("rec_loading"),
      () =>
        new Promise((success, reject) => {
          RecipesRepository.copyRecipe(this.recipeForCopy.id, newRecipeName)
            .then((response) => {
              this.showCopyRecipeModal = false;
              this.$router.push({
                name: "recipesEdit",
                params: { id: `${response.data.id}` },
              });
              success({
                body: this.translations.get("rec_copy_recipe_success"),
              });
            })
            .catch((error: ErrorResponse) => {
              this.showCopyRecipeModal = false;
              let message;

              if (error.response) {
                message = error.response.data.message;
              }
              else {
                message = "Unknown error while copying item.";
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3500,
        closeOnClick: true,
      }
    );
  }

  /**
   * Method which requests delete recipes to API and shows toastr message
   */
  removeRecipes() {
    this.funcHandler(this.checkedRecipesForDelete);
    this.showDeleteRecipeModal = false;
  }

  /**
   * Computed method which returns URL based on the current environment
   *
   * @return {string} url
   */
  get exportToExcelURL() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes${recipeResource}/reports/ExportRecipes?RecipesReportType=${this.exportType}&country=${this.exportCountryType}`;
  }

  auditLogModal = {
    isOpen: false
  };

  get translations() {
    return this.localizationState.translations;
  }

  get isTrial() {
    return this.$store.getters.getUserDetails.isTrial;
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["common", "recipes", "ingredients"],
      })
      .then(() => {
        document.title =
          this.translations.get("rec_title") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }
}
export default Recipes;
